export function normalizeChartHistory(raw) {
  if (raw == null) return null;

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return { value: trimmed };
    }
  }

  if (typeof raw === "object") {
    return raw;
  }

  return null;
}

export function formatChartHistoryFieldLabel(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function formatChartHistoryDisplayText(text) {
  if (text == null || text === "" || text === "—") {
    return text ?? "—";
  }

  return String(text)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?p>/gi, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function formatScalarValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string") return formatChartHistoryDisplayText(value);
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function isArrayOfObjects(value) {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => item && typeof item === "object" && !Array.isArray(item))
  );
}

/**
 * @returns {Array<{ field: string, label: string, raw: unknown, text: string|null, isNestedTable: boolean }>}
 */
export function getChartHistoryRows(chartHistory) {
  const data = normalizeChartHistory(chartHistory);
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return [];
  }

  return Object.entries(data).map(([field, raw]) => ({
    field,
    label: formatChartHistoryFieldLabel(field),
    raw,
    text: isArrayOfObjects(raw) ? null : formatScalarValue(raw),
    isNestedTable: isArrayOfObjects(raw),
  }));
}

/**
 * Summary value for chart_history.is_prechart: "true", "false", or "N/A".
 * @param {*} chartHistory
 * @returns {"true" | "false" | "N/A"}
 */
export function getChartHistoryIsPrechartSummary(chartHistory) {
  const data = normalizeChartHistory(chartHistory);
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return "N/A";
  }

  if (!Object.prototype.hasOwnProperty.call(data, "is_prechart")) {
    return "N/A";
  }

  const value = data.is_prechart;
  if (value === true || value === "true") return "true";
  if (value === false || value === "false") return "false";

  return "N/A";
}

/** Whether patient.chart_history has parseable, non-empty content. */
export function isChartHistoryPresent(chartHistory) {
  const data = normalizeChartHistory(chartHistory);
  if (!data) return false;

  if (typeof data === "object" && !Array.isArray(data)) {
    return Object.keys(data).length > 0;
  }

  return true;
}

export function getChartHistoryFieldCount(chartHistory) {
  const data = normalizeChartHistory(chartHistory);
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return 0;
  }
  return Object.keys(data).length;
}
